import { i18n } from '../i18n.js';
/**
 * InviteTicket — the arrival screen for an invited player.
 *
 * A referral link (`/play?ref=CODE`) used to drop a newcomer straight onto the
 * sign-in view, which is the least persuasive screen in the product: it asks
 * before it gives. The pattern this replaces it with is the one Cloudflare's
 * wallet campaign and the Comet invite flow use — the arriving person is shown
 * an OBJECT that is already theirs, and the only action is to accept it.
 *
 * Design notes, deliberately recorded:
 *
 * - The ticket is a physical thing, so it behaves like one. It reuses the card
 *   tilt engine (`card-tilt.js`) rather than a bespoke transform, so its weight,
 *   glare and easing match every card in the game. One physics, one feel.
 * - It is PRESENTATION ONLY. The referral itself is already recorded
 *   server-side from the `ref` parameter, and the reward terms are whatever the
 *   server already grants. This screen grants nothing, promises nothing the
 *   server has not, and cannot be used to mint anything: "Accept" simply
 *   dismisses it and hands the player to the normal auth flow.
 * - It shows ONCE per code per browser. An invite that re-announces itself on
 *   every visit stops reading as a gift and starts reading as an ad.
 * - It never blocks: no network call gates it, and any failure to build it
 *   leaves the player on the ordinary sign-in path.
 *
 * Contract: tests/unit/ui/inviteTicket.test.js
 */
import { Firebase } from '../firebase-loader.js';
import { CardTilt } from './card-tilt.js';
import {
  buildInviteTicket,
  holdBootVeilForTicket,
  relocalizeInviteTicket,
  releaseBootVeilForTicket,
  warmInviteTicketArt,
  whenInviteTicketWhole,
} from './invite-ticket-object.js';
import { SoundEffects } from './sound-effects.js';
import { MoonfallReferralLink } from '../referral-link.js';
import { MoonfallGuest } from '../guest-session.js';
let InviteTicket = {};

(function () {

  const SEEN_KEY_PREFIX = 'moonfall.invite.seen.';
  const ROOT_ID = 'invite-ticket-view';

  function uiText(key, fallback, replacements) {
    try {
      const value = i18n?.t?.(key, replacements);
      if (typeof value === 'string' && value && value !== key) return value;
    } catch (_) { /* i18n not ready */ }
    if (!replacements) return fallback;
    return Object.keys(replacements).reduce(
      (text, token) => text.replace(new RegExp(`{{${token}}}`, 'g'), replacements[token]),
      fallback,
    );
  }

  const CODE_SHAPE = /^[A-Za-z0-9_-]{1,32}$/;

  /**
   * The invite code for this arrival. Never trusted for anything but display.
   *
   * READ THE CAPTURED CODE FIRST, not the URL.
   * `referral-link.js` loads earlier in index.html and, by design, moves the
   * code out of the address bar the moment it sees it: it stashes it in
   * sessionStorage and strips `ref` from the URL, so a bearer-like invitation
   * code cannot leak into screenshots, copied links or subsequent Referer
   * headers. That is correct and must stay.
   *
   * This screen used to read `location.href` directly, which meant it was
   * RACING that strip — it rendered only when it happened to run first, and
   * showed nothing when it did not. Intermittent by construction, and the most
   * likely explanation for the whole feature being reported "fully missing".
   * The captured value is the authority; the URL is only a fallback for the
   * case where capture could not store anything (private modes throw).
   */
  function referralCode() {
    try {
      const captured = MoonfallReferralLink?.getPendingCode?.();
      if (typeof captured === 'string' && CODE_SHAPE.test(captured)) return captured;
    } catch (_) { /* capture module absent or storage blocked */ }
    try {
      const code = new URL(window.location.href).searchParams.get('ref');
      if (!code) return null;
      // Display-only, and the server is the authority on validity.
      return CODE_SHAPE.test(code) ? code : null;
    } catch (_) {
      return null;
    }
  }

  /* Private browsing throws on localStorage, or accepts a write and keeps
     nothing. The once-per-code guard is the only thing standing between this
     screen and re-covering the app on every navigation that carries a `ref`, so
     it falls back to memory for the life of the page. An invite that
     re-announces itself forever stops reading as a gift. */
  const seenThisPage = new Set();

  function alreadySeen(code) {
    if (seenThisPage.has(code)) return true;
    try { return window.localStorage?.getItem(SEEN_KEY_PREFIX + code) === '1'; } catch (_) { return false; }
  }

  function markSeen(code) {
    try {
      window.localStorage?.setItem(SEEN_KEY_PREFIX + code, '1');
      /* Read BACK. Private modes have historically accepted a write and kept
         nothing, which looks like success and silently disarms the guard. */
      if (window.localStorage?.getItem(SEEN_KEY_PREFIX + code) === '1') return;
    } catch (_) { /* private mode, or storage disabled entirely */ }
    /* Persistence is unavailable, so remember it for the life of the page. The
       guard is the only thing standing between this screen and re-covering the
       app on every navigation that carries a `ref`. */
    seenThisPage.add(code);
  }

  /** The month the ticket was issued, e.g. "AUGUST / 2026". Dating the artefact
      is what turns a modal into a keepsake — a ticket from the first month is a
      status claim that only works if the date is printed on it. */


  function build(code) {
    const root = document.createElement('div');
    root.id = ROOT_ID;
    root.className = 'invite-ticket-view';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-labelledby', 'invite-ticket-title');

    /* THE BRAND, top-left. An invite link is the first Moonfall screen a person
       ever sees, and until now nothing on it said whose ticket this is — the
       artwork is beautiful but unlabelled. The landing page and the auth screen
       both lead with this mark; the arrival screen should too. Decoration only:
       `alt` is empty and it is aria-hidden, because the accessible name of this
       dialog is already its title. */
    const brand = document.createElement('img');
    brand.className = 'invite-ticket-brand';
    brand.src = '/assets/images/logo.webp';
    brand.alt = '';
    brand.decoding = 'async';
    brand.setAttribute('aria-hidden', 'true');
    root.appendChild(brand);

    const stage = document.createElement('div');
    stage.className = 'invite-ticket-stage';

    /* ONE BUILDER, TWO SURFACES. The invitation dialog the sender opens shows
       this same object; it used to render a bare picture of the artwork with
       none of the printing on it. See ui/invite-ticket-object.js. */
    const seededInviter = /** @type {HTMLMetaElement|null} */ (
      document.querySelector('meta[name="moonfall:inviter"]')
    )?.content?.trim() || null;
    const built = buildInviteTicket({ code, issuedBy: seededInviter });
    const { ticket, art, face } = built;

    stage.appendChild(ticket);

    const copy = document.createElement('div');
    copy.className = 'invite-ticket-copy';

    const title = document.createElement('h1');
    title.id = 'invite-ticket-title';
    title.className = 'invite-ticket-title';
    title.setAttribute('data-i18n', 'invite.ticketTitle');
    title.textContent = uiText('invite.ticketTitle', 'This ticket is yours');

    const body = document.createElement('p');
    body.className = 'invite-ticket-body';
    body.setAttribute('data-i18n', 'invite.ticketBody');
    body.textContent = uiText(
      'invite.ticketBody',
      'A friend saved you a seat at Moonfall. Accept it to claim your welcome rewards.',
    );

    const accept = document.createElement('button');
    accept.type = 'button';
    accept.id = 'invite-ticket-accept';
    accept.className = 'invite-ticket-accept game-command-btn';
    accept.setAttribute('data-i18n', 'invite.ticketAccept');
    accept.textContent = uiText('invite.ticketAccept', 'Accept your ticket');

    /* WHAT THE REWARD IS, AND WHAT UNLOCKS IT.
       The packs do not arrive at signup — they land after real matches. An
       invited player told only "welcome rewards" who then sees nothing in their
       balance reasonably concludes the product is broken; that is exactly what
       happened. The numbers come from the server's LIVE configuration, seeded
       into the page, so this promise cannot drift from what is actually
       granted. If the server did not resolve them, the line is omitted rather
       than guessed. */
    let termsLine = null;
    const seededTerms = /** @type {HTMLMetaElement|null} */ (
      document.querySelector('meta[name="moonfall:invite-terms"]')
    )?.content;
    const [termsPacks, termsMatches] = String(seededTerms || '').split('|');
    /* `'0'` is a truthy STRING, which is how an unresolved lookup once printed
       "0 packs land once you finish your first 0 matches" at an arriving
       player. A promise is stated only when there is one. */
    if (Number(termsPacks) > 0 && Number(termsMatches) > 0) {
      const terms = document.createElement('p');
      terms.className = 'invite-ticket-terms';
      /* NO `data-i18n` HERE. This string INTERPOLATES, and `applyTranslations()`
         re-applies a `data-i18n` element's raw translation with no replacements
         — which printed a literal "{{packs}} card packs ... {{matches}} matches"
         on screen. Elements whose copy carries placeholders are re-localized by
         `relocalize()` below, which passes the values. */
      terms.dataset.inviteTerms = `${termsPacks}|${termsMatches}`;
      terms.textContent = uiText(
        'invite.ticketTerms',
        '{{packs}} card packs land once you finish your first {{matches}} matches.',
        { packs: termsPacks, matches: termsMatches },
      );
      termsLine = terms;
    }

    /* Reading order: what this is, who sent it, what you get, then the one
       thing to do. The terms sit between the promise and the button so they are
       read before accepting, not after. */
    copy.append(title, body);
    if (termsLine) copy.appendChild(termsLine);
    copy.appendChild(accept);
    root.append(stage, copy);

    /* THE TICKET RENDERS BEFORE i18n RESOLVES, SO IT MUST RE-LOCALIZE.
       An invite link is the very first thing an invited player loads, and this
       screen is built during that load — earlier than i18n finishes fetching
       `ui.json`. Every `uiText()` above therefore returned its English fallback,
       and `data-i18n` did not help either, because `applyTranslations()` had
       already run over a page that did not yet contain these elements. Result:
       the whole ticket was English for every non-English player, permanently.
       Nothing ever caught it because the browser spec only ever ran in English.

       Every other localized surface in the app already listens for these two
       events (card-packs, collection, moon-exchange); the ticket simply never
       did. Re-applying is cheap, idempotent, and changes no geometry that the
       arrival spec does not assert. */
    const relocalize = () => {
      try {
        i18n?.applyTranslations?.();
        /* The ticket's own printing belongs to the builder that made it, and
           it only rewrites the issued-by line when the lookup has answered --
           otherwise it would overwrite the empty placeholder with a half-built
           line. */
        relocalizeInviteTicket(root);
        /* Same reason as the issuer: placeholders must be substituted, so this
           cannot ride on `data-i18n`. */
        const termsEl = /** @type {HTMLElement|null} */ (
          root.querySelector('.invite-ticket-terms')
        );
        const seeded = termsEl?.dataset.inviteTerms;
        if (termsEl && seeded) {
          const [packs, matches] = seeded.split('|');
          termsEl.textContent = uiText(
            'invite.ticketTerms',
            '{{packs}} card packs land once you finish your first {{matches}} matches.',
            { packs, matches },
          );
        }
      } catch (_) { /* localization is decoration; never let it break the ticket */ }
    };
    window.addEventListener('moonfall:i18nready', relocalize);
    window.addEventListener('moonfall:languagechange', relocalize);
    const stopLocalizing = () => {
      window.removeEventListener('moonfall:i18nready', relocalize);
      window.removeEventListener('moonfall:languagechange', relocalize);
    };

    /* If i18n already finished before this screen mounted, the events have been
       and gone — apply once directly. */
    /* The global is `window.i18n`, LOWERCASE. Written as `window.I18n` this
       branch silently evaluated to undefined, so whenever i18n won the race and
       finished before this screen mounted, its ready event had already fired and
       nothing re-localized — the ticket kept its English fallbacks. It failed
       only on those loads, which is what made it look like a flaky test. */
    if (i18n?.isInitialized?.()) relocalize();

    return { root, ticket, accept, face, art, stopLocalizing };
  }

  /**
   * INTEGRATION — the fourth beat.
   *
   * `dopamine-maxxing` names four beats for any reward moment: cue,
   * anticipation, resolution, integration. The first three were here; the
   * fourth was not. Accepting simply faded the screen out, so the ticket did
   * not visibly BECOME anything — and a reward that does not join the player's
   * world is half a reward, which that skill calls the most common Moonfall
   * miss.
   *
   * So accepting sends the ticket INTO the game rather than nowhere: it lifts,
   * tilts, and travels off toward the board the player is about to be handed,
   * while the veil clears behind it. Presentation only — the referral was
   * already recorded server-side from the `ref` parameter, this grants nothing,
   * and the removal is guaranteed by a timer regardless of whether any
   * animation runs.
   */
  /**
   * Ask the server who sent this ticket. Decoration only.
   *
   * Deliberately fire-and-forget with a short abort: the arrival screen is
   * already on the player's screen before this starts, so nothing waits on it.
   * Any failure — offline, rate limited, unknown code, no username set — leaves
   * the ticket exactly as it rendered.
   */
  function fillInviter(root, code) {
    if (!root || !code || typeof fetch !== 'function') return Promise.resolve();
    let abort = null;
    try { abort = new AbortController(); setTimeout(() => abort.abort(), 4000); } catch (_) { abort = null; }
    return fetch(`/api/referral/inviter?ref=${encodeURIComponent(code)}`, {
      signal: abort ? abort.signal : undefined,
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const inviter = data && typeof data.inviter === 'string' ? data.inviter.trim() : '';
        if (!inviter || !root.isConnected) return;
        const slot = root.querySelector('.invite-ticket-issuer');
        if (slot) {
          /* Kept so `relocalize()` can re-render this line in a new language
             without re-querying the server. */
          slot.dataset.inviter = inviter.toUpperCase();
          slot.textContent = uiText('invite.ticketIssuedBy', 'ISSUED BY {{name}}', { name: slot.dataset.inviter });
          slot.classList.add('invite-ticket-issuer--filled');
        }
        const body = root.querySelector('.invite-ticket-body');
        if (body) {
          body.textContent = uiText(
            'invite.ticketBodyNamed',
            '{{name}} saved you a seat at Moonfall. Accept it to claim your welcome rewards.',
            { name: inviter },
          );
          body.removeAttribute('data-i18n');
        }
      })
      .catch(() => { /* a name is a nicety; its absence changes nothing */ });
  }

  /**
   * ACCEPTING GOES TO ACCOUNT CREATION, NOT INTO THE TUTORIAL.
   *
   * An anonymous visitor to `/play` auto-starts the guest tutorial, which is why
   * this screen mounts over whatever the app is already booting. Dismissing it
   * therefore used to REVEAL that tutorial — "accepting it should not transition
   * to tutorial. it should skip tutorial. it shoudl transition to account
   * creation". An invited player was handed a ticket and then dropped into a
   * mulligan, which is the wrong next step for someone who has just been given a
   * seat: the seat is worth nothing until they have an account to keep it in.
   *
   * `endGuestTutorial('skip')` is the product's own path for exactly this. It
   * deactivates the guest session, remembers the skip so the tutorial does not
   * re-start, tears the tutorial UI down so the sign-in moment is not drawn over
   * a half-lit board, and presents that moment. Using it rather than reaching
   * into the auth view directly keeps one owner for the transition.
   *
   * A player who is ALREADY signed in is left exactly where they were — they
   * have an account, so there is nothing to create, and forcing a sign-in moment
   * on them would be a regression.
   */
  function acceptInvite(root) {
    let signedIn = false;
    try { signedIn = Firebase?.isSignedIn?.() === true; } catch (_) { signedIn = false; }
    if (!signedIn) {
      /* `window.MoonfallGuest`, NOT `GuestSession` — the module names its
         export `MoonfallGuest` (`public/scripts/guest-session.js`). Written the
         other way this whole hand-off was a silent no-op: optional chaining on
         an undefined global throws nothing, so the ticket dismissed cleanly and
         dropped the player straight back into the tutorial it was supposed to
         skip. Verified on the running app: `typeof window.GuestSession` is
         "undefined". */
      try { MoonfallGuest?.endGuestTutorial?.('skip'); } catch (_) {
        /* Never let the hand-off failing trap the player behind the ticket. */
      }
    }
    dismiss(root);
  }

  function dismiss(root) {
    settleArrival();
    if (!root || !root.isConnected) return;
    const ticket = root.querySelector('.invite-ticket');
    if (ticket) ticket.classList.add('invite-ticket--claimed');
    root.classList.add('invite-ticket-view--leaving');
    /* Drop the i18n listeners with the screen. They are cheap, but a live
       listener holding a detached subtree is a leak, and this screen can mount
       again on a later arrival. */
    const remove = () => {
      try { root.__inviteStopLocalizing?.(); } catch (_) { /* already gone */ }
      root.remove();
    };
    if (typeof root.addEventListener === 'function') {
      root.addEventListener('animationend', remove, { once: true });
    }
    // Never leave the app behind a screen because an animation did not fire.
    setTimeout(remove, 600);
  }

  /**
   * Show the arrival screen, if this visit warrants one.
   * @returns {boolean} whether it was shown
   */
  /**
   * Did the SERVER confirm this invite exists?
   *
   * The code's shape is not evidence. `?ref=YOUR_CODE` matches the shape check
   * below, so a made-up link used to raise a ticket announcing that a seat had
   * been saved by someone who does not exist — a fabricated state, and the
   * first thing a curious visitor tries. `/play?ref=CODE` resolves the code
   * server-side and emits this tag only when it is real.
   *
   * The tag is absent when the page was NOT served with a code — a stored code
   * after `referral-link.js` stripped the URL, or a private-mode fallback. That
   * case is handled by `confirmStoredCode` below rather than by assuming.
   */
  function serverConfirmedInvite() {
    return !!document.querySelector('meta[name="moonfall:invite"]');
  }

  /**
   * A PHYSICAL OBJECT ARRIVES WHOLE.
   *
   * The ticket used to be appended the instant it was built, so the player
   * watched it ASSEMBLE: the heading and the Accept button faded up first, the
   * corner stamps printed into empty space where the ticket was going to be,
   * the body read "A friend saved you a seat", and only then — 400ms warm, over
   * three seconds on a throttled connection — did the 326KB artwork arrive
   * underneath them, followed by the real inviter's name replacing "a friend".
   * Four states for one object. Nothing that behaves like a physical thing
   * assembles itself in front of you, and the illusion this whole screen exists
   * to create was spent before the player could look at it.
   *
   * So the element mounts immediately (it is never withheld, and the tilt engine
   * still measures it) but stays UNPAINTED until the artwork has decoded and the
   * inviter's name has landed. Then one entrance animation carries the finished
   * object in.
   *
   * HARD CAP, because a readiness gate that can fail to open has deleted the
   * feature it was warming up: whatever has not resolved by then is revealed as
   * it stands. A missing name reads as "a friend", which is true, and a missing
   * artwork still leaves an accept button. Neither is worth withholding a
   * screen for.
   */
  /**
   * ONE SCREEN, NOT TWO.
   *
   * An invited arrival used to watch the app boot normally first: the veil
   * dropped, "Sign in to Play" painted, and only then -- after the round-trip
   * that confirms the code is real -- did the ticket slam down on top of it.
   * Measured on a throttled load: the auth view was on screen for most of a
   * second before the ticket covered it. It reads as a bug, because it is one:
   * the player is shown a screen that was never meant for them.
   *
   * So while an invite code is present and unresolved, the boot veil is asked
   * to stay. `main.js` reads this flag and waits for `moonfall:invitesettled`.
   *
   * HARD CAP again, and it matters more here than anywhere: this flag holds the
   * WHOLE APP behind a loading screen. If the confirm hangs, the flag clears
   * itself and the app boots normally.
   */
  /**
   * THE ARTWORK STARTS DOWNLOADING WITH THE APP, NOT WITH THE TICKET.
   *
   * 326KB of ornate filigree and starfield, which re-encoding does not shrink
   * (it is already packed about as tightly as that much detail goes -- lossy
   * passes come out LARGER). The problem was never its size, it was WHEN it
   * started: the fetch began when the ticket mounted, which is after the app
   * has booted and after the round-trip that confirms the code. On a throttled
   * link that put it three seconds behind everything else, so the readiness cap
   * expired and the ticket appeared without its own body.
   *
   * Kicked off here instead, the moment we know an invite is pending, it is
   * long since cached by the time the ticket is built. Only invited arrivals
   * pay for it; nobody else fetches it at all.
   */
  /* The warm, the veil hold and the decode wait are shared with the voucher
     arrival and the invitation dialog (invite-ticket-object.js); this module
     only decides WHEN each starts. */
  const VEIL_OWNER = 'invite';
  const ARRIVAL_HOLD_CAP_MS = 10000;
  let artPreload = null;

  function preloadTicketArt() {
    if (!artPreload) artPreload = warmInviteTicketArt();
    return artPreload;
  }

  function settleArrival() {
    releaseBootVeilForTicket(VEIL_OWNER);
  }

  function holdArrival() {
    try {
      if (!referralCode()) return;
      holdBootVeilForTicket(VEIL_OWNER, { capMs: ARRIVAL_HOLD_CAP_MS });
      preloadTicketArt();
    } catch (_) { /* the registry's own cap still releases it */ }
  }

  /*
   * Long, deliberately. The alternative to waiting is not "the player sees the
   * ticket sooner" -- it is "the player sees a half-drawn one", which is the
   * defect this whole gate exists to remove. While the gate is closed the boot
   * veil is still up, so what the player actually sees is an honest loading
   * spinner rather than a broken object. Measured on a 900kbps link the artwork
   * takes ~8s to arrive; on anything ordinary it is cached before the confirm
   * round-trip even returns, and this cap is never reached.
   */
  const PRESENT_CAP_MS = 9000;

  function whenWhole(art, inviterFilled) {
    return whenInviteTicketWhole(art, {
      capMs: PRESENT_CAP_MS,
      extra: [artPreload, inviterFilled],
    });
  }

  function maybeShow({ force = false, confirmed = serverConfirmedInvite() } = {}) {
    const code = referralCode();
    if (!code) return false;
    /* No confirmation, no claim. */
    if (!confirmed) return false;
    /* THE PHANTOM-INVITE GUARD. An established account is never this
       ticket's audience — the seat it offers is FIRST SIGNUP, which this
       browser's account already spent elsewhere. The pending code lives in
       sessionStorage, which iOS restores with the tab indefinitely, so a code
       captured for one visitor kept resurfacing for whoever signed in next:
       "i tried to login with support moonfall and it started with the game
       invite from velvetvale97". Auth that restores AFTER the ticket mounted
       is handled by the subscriber below. */
    if (Firebase?.isSignedIn?.() === true) return false;
    /* Arriving at an invite link IS the intent to see the invite. The guard used
       to suppress it for good after one visit, so a player who opened the link
       once — or an owner checking their own campaign — never saw it again and
       reported the whole feature "fully missing". A ticket is dismissed with one
       tap; it does not need protecting from the person who clicked it.

       `alreadySeen` still exists for the SAME-PAGE case below: it stops a second
       ticket stacking over the first within one visit. */
    if (!force && document.getElementById(ROOT_ID)) return false;
    if (document.getElementById(ROOT_ID)) return false;
    if (!document.body) return false;

    const { root, ticket, accept, art, stopLocalizing } = build(code);
    /** @type {any} */ (root).__inviteStopLocalizing = stopLocalizing;
    /* Mounted now, painted later — see `whenWhole`. */
    root.classList.add('invite-ticket-view--holding');
    document.body.appendChild(root);
    markSeen(code);

    /* One physics for everything that behaves like a card. Registering with the
       shared tilt engine — rather than writing another transform here — is what
       makes the ticket feel like the same material as the cards it is inviting
       the player to collect. Presentation only; it never gates the accept. */
    /* HAND TRANSFORM BACK TO THE TILT ENGINE once the entrance is done.
       The arrival animation runs with `fill-mode: both`, so it KEEPS owning
       `transform` at its final keyframe forever — and an animation beats the
       inline transform CardTilt writes. The ticket registered fine and simply
       could not move: "not 3d rotatable based on mouse". Clearing the animation
       at animationend leaves it exactly at its rest pose (the 100% keyframe is
       the identity transform), so nothing jumps and the tilt takes over. */
    const releaseTransform = () => ticket.classList.add('invite-ticket--settled');
    ticket.addEventListener('animationend', releaseTransform, { once: true });
    /* Belt and braces: if the animation never fires (reduced motion, a
       backgrounded tab), the ticket must still be tiltable. */
    setTimeout(releaseTransform, 1100);

    try { CardTilt?.registerPreview?.(ticket); } catch (_) { /* tilt is decoration */ }

    /* THE BEATS. Cue and anticipation are the ticket's own entrance (CSS); this
       schedules RESOLUTION — the serial and the date stamping onto the face
       after the ticket has arrived and settled, rather than being there from
       frame one. A face that is already complete when it lands has no moment of
       "this one is mine"; staggering it costs 260ms and buys the beat.

       Reduced motion resolves immediately: the information is never withheld,
       only its timing changes. */
    const settle = () => {
      try { root.classList.add('invite-ticket-view--resolved'); } catch (_) { /* ignore */ }
      /* The same cue the invite-welcome screen plays for its "this one is
         mine" beat. It used to ask for 'reveal', a name no manifest has ever
         carried, so the stamp landed silent. */
      try { SoundEffects?.play?.('card_reveal'); } catch (_) { /* muted */ }
    };
    let reduced = false;
    try { reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true; }
    catch (_) { reduced = false; }

    const inviterFilled = fillInviter(root, code);

    /* The entrance, the settle beat and the focus all hang off the reveal, not
       off the append — otherwise the 260ms resolution beat burns down while the
       ticket is still invisible and the face lands already-complete. */
    const reveal = () => {
      if (!root.isConnected) return;
      root.classList.remove('invite-ticket-view--holding');
      settleArrival();
      if (reduced) settle();
      else setTimeout(settle, 260);
      try { accept.focus({ preventScroll: true }); } catch (_) { accept.focus(); }
    };
    try {
      whenWhole(art, inviterFilled).then(reveal, reveal);
    } catch (_) {
      reveal();
    }

    accept.addEventListener('click', () => acceptInvite(root));

    // Escape accepts too: this screen must never be a trap.
    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') acceptInvite(root);
    });

    return true;
  }

  InviteTicket = { maybeShow, referralCode, __dismiss: dismiss };

  /* Show it on ARRIVAL, not from the sign-in view.
     An anonymous visitor to /play auto-starts the guest tutorial, so the auth
     view is never painted and a hook there never fires — measured: the invited
     player landed straight in a mulligan. The ticket is the first thing an
     invited arrival should see, so it goes up as soon as there is a body to
     put it in, over whatever the app is otherwise starting. It still shows
     once per code, still grants nothing, and accepting reveals what is already
     running underneath. */
  /**
   * The page was not served with a `?ref=`, but a code is in storage — the
   * ordinary case on any navigation AFTER `referral-link.js` strips the URL.
   * Ask the server whether that code is real before claiming anything. This is
   * the only path that costs a round-trip, and it cannot show a false ticket.
   */
  async function confirmStoredCode(code) {
    try {
      const response = await fetch(`/api/referral/inviter?ref=${encodeURIComponent(code)}`, {
        headers: { accept: 'application/json' },
      });
      if (!response.ok) return false;
      const data = await response.json();
      return data?.exists === true;
    } catch (_) {
      /* Unreachable server: say nothing rather than promise a seat. */
      return false;
    }
  }

  function showOnArrival() {
    try {
      if (maybeShow()) return;
      /* Server did not confirm inline. If there is a stored code, ask. */
      const code = referralCode();
      if (!code) { settleArrival(); return; }
      confirmStoredCode(code).then((confirmed) => {
        if (confirmed) {
          try {
            if (maybeShow({ confirmed: true })) return;
          } catch (_) { /* never block */ }
        }
        /* No ticket is coming: let the app get on with booting. */
        settleArrival();
      }).catch(() => { settleArrival(); });
    } catch (_) { settleArrival(); }
  }

  /* Auth restores asynchronously, usually AFTER showOnArrival has already
     mounted the ticket on a cold load. The moment a user materialises, an
     open ARRIVAL ticket is addressing the wrong person — retire it. (The
     accept flow's own signed-in path never reaches this: accepting removes
     the ticket before any sign-in completes.) */
  try {
    Firebase?.subscribe?.((user) => {
      if (!user) return;
      const root = document.getElementById(ROOT_ID);
      if (root) dismiss(root);
    });
  } catch (_) { /* the guard in maybeShow still covers the common path */ }

  holdArrival();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', showOnArrival, { once: true });
  } else {
    showOnArrival();
  }
}());

export { InviteTicket };
